/**
 * Shared calculations and wealth tier configurations for My Wealth
 */

export interface FinancialAssets {
  cashAndSavings: number;
  investments: number;
  retirementAccounts: number;
  propertyValue: number;
  vehicles: number;
  otherAssets: number;
}

export interface FinancialLiabilities {
  creditCardDebt: number;
  studentLoans: number;
  homeLoans: number;
  otherDebts: number;
}

export interface FinancialCashFlow {
  monthlyIncome: number;
  monthlyExpenses: number;
}

export type WealthTierKey = 'negative' | 'starter' | 'building' | 'comfortable' | 'established' | 'premium';

export interface WealthTierInfo {
  key: WealthTierKey;
  name: string;
  badgeLabel: string;
  description: string;
  minNetWorth: number;
  maxNetWorth: number;
  accentColor: string;
  roomTheme: {
    roomLayout: 'starter' | 'expanded' | 'suite' | 'estate' | 'penthouse';
    wallColor: string;
    floorColor: string;
    rugColor: string;
    lightingMood: string;
    maxDecorations: number;
  };
}

export const WEALTH_TIER_CONFIG: Record<WealthTierKey, WealthTierInfo> = {
  negative: {
    key: 'negative',
    name: 'Reset & Rebuild',
    badgeLabel: 'Focus Zone',
    description: 'Prioritizing debt reduction and building an emergency foundation.',
    minNetWorth: -Infinity,
    maxNetWorth: 0,
    accentColor: '#ef4444',
    roomTheme: {
      roomLayout: 'starter',
      wallColor: '#cbd5e1',
      floorColor: '#94a3b8',
      rugColor: '#64748b',
      lightingMood: 'neutral',
      maxDecorations: 2
    }
  },
  starter: {
    key: 'starter',
    name: 'Starter Haven',
    badgeLabel: 'Starter Tier',
    description: 'Building cash reserves and setting up a positive personal runway.',
    minNetWorth: 0,
    maxNetWorth: 25000,
    accentColor: '#38bdf8',
    roomTheme: {
      roomLayout: 'starter',
      wallColor: '#bae6fd',
      floorColor: '#7dd3fc',
      rugColor: '#0284c7',
      lightingMood: 'warm',
      maxDecorations: 4
    }
  },
  building: {
    key: 'building',
    name: 'Growth Studio',
    badgeLabel: 'Growth Tier',
    description: 'Accelerating asset accumulation and expanding diversified savings.',
    minNetWorth: 25000,
    maxNetWorth: 100000,
    accentColor: '#10b981',
    roomTheme: {
      roomLayout: 'expanded',
      wallColor: '#bbf7d0',
      floorColor: '#86efac',
      rugColor: '#059669',
      lightingMood: 'bright',
      maxDecorations: 6
    }
  },
  comfortable: {
    key: 'comfortable',
    name: 'Comfort Suite',
    badgeLabel: 'Comfort Tier',
    description: 'Resilient net worth with strong monthly surplus and asset stability.',
    minNetWorth: 100000,
    maxNetWorth: 300000,
    accentColor: '#6366f1',
    roomTheme: {
      roomLayout: 'suite',
      wallColor: '#c7d2fe',
      floorColor: '#a5b4fc',
      rugColor: '#4f46e5',
      lightingMood: 'sunlit',
      maxDecorations: 8
    }
  },
  established: {
    key: 'established',
    name: 'Established Estate',
    badgeLabel: 'Estate Tier',
    description: 'Long-term financial security with multi-asset growth and peace of mind.',
    minNetWorth: 300000,
    maxNetWorth: 1000000,
    accentColor: '#a855f7',
    roomTheme: {
      roomLayout: 'estate',
      wallColor: '#e9d5ff',
      floorColor: '#d8b4fe',
      rugColor: '#7e22ce',
      lightingMood: 'ambient',
      maxDecorations: 10
    }
  },
  premium: {
    key: 'premium',
    name: 'Apex Loft',
    badgeLabel: 'Apex Tier',
    description: 'Exceptional financial independence and expansive long-term capital.',
    minNetWorth: 1000000,
    maxNetWorth: Infinity,
    accentColor: '#f59e0b',
    roomTheme: {
      roomLayout: 'penthouse',
      wallColor: '#fde68a',
      floorColor: '#fcd34d',
      rugColor: '#d97706',
      lightingMood: 'golden',
      maxDecorations: 12
    }
  }
};

export function cleanMoneyNumber(val: any): number {
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(val);
  if (isNaN(num) || !isFinite(num) || num < 0) {
    throw new Error('Financial values must be valid non-negative numbers');
  }
  if (num > 1e12) {
    throw new Error('Value exceeds allowable financial ceiling (1 trillion)');
  }
  return Math.round(num * 100) / 100;
}

export function determineWealthTier(netWorth: number, monthlySurplus = 0): WealthTierKey {
  const nw = Number(netWorth) || 0;
  if (nw < 0) return 'negative';
  if (nw < 25000) return 'starter';
  if (nw < 100000) return 'building';
  if (nw < 300000) return 'comfortable';
  if (nw < 1000000) return 'established';
  return 'premium';
}

export interface CalculatedSummary {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  monthlySurplus: number;
  visualizationTier: WealthTierKey;
}

export function calculateFinances(
  assets: Partial<FinancialAssets> = {},
  liabilities: Partial<FinancialLiabilities> = {},
  cashFlow: Partial<FinancialCashFlow> = {}
): {
  assets: FinancialAssets;
  liabilities: FinancialLiabilities;
  cashFlow: FinancialCashFlow;
  calculated: CalculatedSummary;
} {
  const sanitizedAssets: FinancialAssets = {
    cashAndSavings: cleanMoneyNumber(assets.cashAndSavings),
    investments: cleanMoneyNumber(assets.investments),
    retirementAccounts: cleanMoneyNumber(assets.retirementAccounts),
    propertyValue: cleanMoneyNumber(assets.propertyValue),
    vehicles: cleanMoneyNumber(assets.vehicles),
    otherAssets: cleanMoneyNumber(assets.otherAssets)
  };

  const sanitizedLiabilities: FinancialLiabilities = {
    creditCardDebt: cleanMoneyNumber(liabilities.creditCardDebt),
    studentLoans: cleanMoneyNumber(liabilities.studentLoans),
    homeLoans: cleanMoneyNumber(liabilities.homeLoans),
    otherDebts: cleanMoneyNumber(liabilities.otherDebts)
  };

  const sanitizedCashFlow: FinancialCashFlow = {
    monthlyIncome: cleanMoneyNumber(cashFlow.monthlyIncome),
    monthlyExpenses: cleanMoneyNumber(cashFlow.monthlyExpenses)
  };

  const totalAssets = Math.round(
    (sanitizedAssets.cashAndSavings +
      sanitizedAssets.investments +
      sanitizedAssets.retirementAccounts +
      sanitizedAssets.propertyValue +
      sanitizedAssets.vehicles +
      sanitizedAssets.otherAssets) * 100
  ) / 100;

  const totalLiabilities = Math.round(
    (sanitizedLiabilities.creditCardDebt +
      sanitizedLiabilities.studentLoans +
      sanitizedLiabilities.homeLoans +
      sanitizedLiabilities.otherDebts) * 100
  ) / 100;

  const netWorth = Math.round((totalAssets - totalLiabilities) * 100) / 100;
  const monthlySurplus = Math.round((sanitizedCashFlow.monthlyIncome - sanitizedCashFlow.monthlyExpenses) * 100) / 100;
  const visualizationTier = determineWealthTier(netWorth, monthlySurplus);

  return {
    assets: sanitizedAssets,
    liabilities: sanitizedLiabilities,
    cashFlow: sanitizedCashFlow,
    calculated: {
      totalAssets,
      totalLiabilities,
      netWorth,
      monthlySurplus,
      visualizationTier
    }
  };
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  } catch (e) {
    return `$${amount.toLocaleString()}`;
  }
}
